import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import {
	useTranslationCommonUi,
	useTranslationDefault,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { FileExtension } from '@dealstack/shared/lib/common/dataExchange/common.ts';
import { isImageExtension } from '@dealstack/shared/lib/common/file/file.ts';
import { Flex } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import {
	ImageScroll,
	PdfScroll,
	StyledImg,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/ReviewFiles/styled';
import { PDF_DOCUMENT_OPTIONS, PDF_WORKER_SRC } from '@dealstack/frontend/shared/utils/pdfWorker';
import { usePdfDocumentKey } from '@dealstack/frontend/shared/hooks/usePdfDocumentKey';

pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;

interface Props {
	file?: Blob | string;
	fileName: string;
	fileExtension?: string;
	isLoading?: boolean;
	scale: number;
	pageWidth?: number;
	onCurrentPageChange: (page: number) => void;
	onTotalPagesChange: (total: number) => void;
}

export const KycPreviewer = ({
	file,
	fileName,
	fileExtension,
	isLoading,
	scale,
	pageWidth,
	onCurrentPageChange,
	onTotalPagesChange,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const tDefault = useTranslationDefault();

	const isImage = isImageExtension(fileExtension as FileExtension);
	const [totalPages, setTotalPages] = useState(1);
	const documentKey = usePdfDocumentKey(file, fileName);

	const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (!file || !isImage) {
			setImageSrc(undefined);
			return;
		}
		if (typeof file === 'string') {
			setImageSrc(file);
			return;
		}
		const url = URL.createObjectURL(file);
		setImageSrc(url);
		return () => URL.revokeObjectURL(url);
	}, [file, isImage]);

	useEffect(() => {
		if (!isImage) {
			return;
		}
		setTotalPages(1);
		onTotalPagesChange(1);
		onCurrentPageChange(1);
	}, [isImage, onTotalPagesChange, onCurrentPageChange]);

	const pagesScrollRef = useRef<HTMLDivElement>(null);
	const pagesRefs = useRef<Record<string, HTMLDivElement | null>>({});

	useEffect(() => {
		const container = pagesScrollRef.current;
		if (!container || isImage) {
			return;
		}

		const onScroll = () => {
			const containerTop = container.getBoundingClientRect().top;
			let topMostPage = 1;
			let minDistance = Infinity;
			for (let i = 1; i <= totalPages; i += 1) {
				const pageEl = pagesRefs.current[i];
				if (!pageEl) {
					continue;
				}
				const rect = pageEl.getBoundingClientRect();
				const distance = Math.abs(rect.top - containerTop);
				if (distance < minDistance) {
					minDistance = distance;
					topMostPage = i;
				}
			}
			onCurrentPageChange(topMostPage);
		};

		container.addEventListener('scroll', onScroll, { passive: true });
		onScroll();
		return () => container.removeEventListener('scroll', onScroll);
	}, [onCurrentPageChange, totalPages, isImage]);

	if (isImage) {
		return (
			<ImageScroll>
				{isLoading || !imageSrc ? (
					<InlineLoader />
				) : (
					<StyledImg src={imageSrc} $scale={scale} alt={fileName} />
				)}
			</ImageScroll>
		);
	}

	const emptyStateFallback = (
		<EmptyState
			icon="es-box-magnifying-glass"
			title={tDefault('no_data')}
			description={tCommonUi('pdf_loader.failed_load')}
			style={{ minWidth: '500px' }}
		/>
	);

	return (
		<PdfScroll ref={pagesScrollRef}>
			{isLoading || !file ? (
				<InlineLoader />
			) : (
				<Document
					options={PDF_DOCUMENT_OPTIONS}
					key={documentKey}
					file={file}
					onLoadSuccess={({ numPages }) => {
						setTotalPages(numPages);
						onTotalPagesChange(numPages);
					}}
					loading={<InlineLoader />}
					error={emptyStateFallback}
					noData={emptyStateFallback}
				>
					<Flex direction="column" gap="0.5rem" pb="1rem">
						{Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
							<div
								key={pageNumber}
								id={pageNumber.toString()}
								ref={(el) => (pagesRefs.current[pageNumber] = el)}
							>
								<Page
									scale={scale}
									pageNumber={pageNumber}
									renderTextLayer={false}
									width={pageWidth}
								/>
							</div>
						))}
					</Flex>
				</Document>
			)}
		</PdfScroll>
	);
};
